/* storybook-check-ignore */
import React, { useState, useMemo, useEffect } from 'react';
import { Modal, ModalProps } from '@opendoor/bricks/complex/Modal';
import { Button, Card, Flex, Image, Input, Text } from '@opendoor/bricks/core';
import { exclusives } from '@opendoor/bricks/theme';
import moment, { Moment, Duration } from 'moment-timezone';
import { Listing } from '../../../declarations/exclusives/listing';
import useExclusiveAnalytics from '../hooks/useExclusiveAnalytics';
import { doBFFPost } from '../../api';
import { EXCLUSIVE_RAILS_HOST } from '../../globals';
import ErrorModal from '../ErrorModal';
import { useRouter } from 'next/router';
import pick from 'lodash/pick';
import { useListing } from '../hooks/useListing';

const AMOUNT_OF_NEXT_DAYS = 15;

function getDays(today: Moment) {
  let date = today;
  const days: Moment[] = [];
  while (days.length != AMOUNT_OF_NEXT_DAYS) {
    days.push(date);
    date = date.clone().add(1, 'day');
  }
  return days;
}

function SelectableCard(
  props: React.PropsWithChildren<{ isSelected: boolean; onClick: () => void }>,
) {
  return (
    <Button
      variant="ghost"
      analyticsName=""
      aria-label=""
      onClick={props.onClick}
      backgroundColor="transparent"
    >
      <Card
        boxShadow="none"
        variant="secondary"
        boxSizing="border-box"
        border={
          props.isSelected
            ? '3px solid var(--colors-brand50)'
            : '1px solid var(--colors-neutrals40)'
        }
        style={{ scrollMarginRight: '1rem', scrollMarginLeft: '1rem' }}
      >
        {props.children}
      </Card>
    </Button>
  );
}

function SelfTourNow() {
  const router = useRouter();
  const listing = useListing();
  const { trackExclusivesEvent } = useExclusiveAnalytics();

  const reportEvent = (label: string) => {
    const fromQuery = pick(router.query, ['market', 'slug', 'source']);
    const payload = { ...fromQuery, type: listing?.exclusive_listing_info.state };
    trackExclusivesEvent('exclusives-open-house', label, undefined, payload);
  };

  useEffect(() => {
    reportEvent('unlock-instructions-viewed');
  }, [trackExclusivesEvent]);

  const onCtaClick = () => {
    reportEvent('unlock-cta-clicked');
  };

  return (
    <Flex flex={1} height="100%" flexDirection="column" justifyContent="space-between">
      <Flex flexDirection="column" gridGap="3rem">
        <Flex flexDirection="column">
          <Text fontWeight="semibold" fontSize="s1" lineHeight="s1">
            How to self-tour this home
          </Text>
          <Text fontSize="s1" lineHeight="s1">
            Welcome! To unlock the door for a self tour, we need to complete a short verification
            over the phone to confirm your identity.
          </Text>
        </Flex>
        <Text fontWeight="semibold" fontSize="s1" lineHeight="s1">
          Verification will take 1-2 minutes.
        </Text>
      </Flex>
      <Flex flexDirection="column" gridGap="0.5rem">
        <Text textAlign="center" flex={1} fontSize="s0" lineHeight="s0">
          Call (855) 476-0049 to unlock the home
        </Text>
        <Button
          as="a"
          borderRadius="rounded"
          onClick={onCtaClick}
          analyticsName="cosmos-exclusives-self-tour-button"
          href="tel:+1 (855) 476-0049"
          // @ts-expect-error TODO @exclusives to resolve
          backgroundColor={exclusives.colors.brand50}
          aria-label={null}
        >
          Call to verify
        </Button>
      </Flex>
    </Flex>
  );
}

const INTERVAL_IN_MINUTES_AT_TIME_RANGE = 30;

const AVAILABLE_TIME_RANGE: Array<Duration> = new Array(23).fill(null).reduce(
  (acc, _, idx) => {
    const prevTime: Duration = acc[idx];
    const nextTime = prevTime.clone().add(INTERVAL_IN_MINUTES_AT_TIME_RANGE, 'minutes');
    acc.push(nextTime);
    return acc;
  },
  [moment.duration(8, 'hours')] as Duration[],
);

// Workaround to properly format the Duration using the AM/PM notation
function timeFormat(time: Duration) {
  if (time.hours() < 12) {
    return time.format('h[:]mm[am]');
  }
  return time.hours() > 12
    ? time.clone().subtract(12, 'hours').format('h[:]mm[pm]')
    : time.format('h[:]mm[pm]');
}

function HomeSelfTourModal(props: ModalProps & { listing: Listing }) {
  const router = useRouter();
  const listing = useListing();
  const today = useMemo(() => moment(), []);
  const days = useMemo(() => getDays(today), [today]);
  const [selfTourNow, setSelfTourNow] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Moment | null>(days[0]);
  const [selectedTime, setSelectedTime] = useState<Duration | null>();
  const [phoneNumber, setPhoneNumber] = useState<string | null>();
  const [hasError, setHasError] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState<boolean>(false);
  const { trackExclusivesEvent } = useExclusiveAnalytics();

  useEffect(() => setSelectedTime(null), [selectedDay]);

  const isToday = useMemo(() => today.isSame(selectedDay), [selectedDay, today]);
  const [timeRange, todayTime] = useMemo(() => {
    const todayTime = moment.duration({ hours: today.hours(), minutes: today.minutes() });
    const todayTimeMinutes = todayTime.asMinutes();
    return [
      isToday
        ? AVAILABLE_TIME_RANGE.filter(
            (time) => todayTimeMinutes && time.asMinutes() > todayTimeMinutes,
          )
        : AVAILABLE_TIME_RANGE,
      todayTime,
    ];
  }, [isToday, today]);
  const isNowSelected = isToday && todayTime === selectedTime;

  const scheduleReminder = async () => {
    setIsScheduleLoading(true);

    if (selectedDay && selectedTime && !isNowSelected) {
      try {
        const timeOfTour = selectedDay
          .hour(selectedTime.hours())
          .minutes(selectedTime.minutes())
          .seconds(0)
          .milliseconds(0);

        const payload = {
          time_of_tour: timeOfTour.toISOString(),
          listing_id: props.listing.id,
          phone_number: phoneNumber,
        };

        const resp = await doBFFPost(
          `${EXCLUSIVE_RAILS_HOST}/api/v1/exclusives/self-tour-reminder`,
          payload,
        );

        if (!resp.ok) {
          throw 'Unsuccesfull status code received at the self-tour-reminder endpoint';
        }

        const eventPayloadFromQuery = {
          ...pick(router.query, ['market', 'source', 'slug']),
          type: listing?.exclusive_listing_info.state,
        };

        trackExclusivesEvent('exclusives-open-house', 'self-tour-scheduled', undefined, {
          ...eventPayloadFromQuery,
          time: timeOfTour.toISOString(),
        });

        props.onRequestClose?.();
      } catch (e) {
        setHasError(true);
      }
    }

    setIsScheduleLoading(false);
  };
  const title = (
    <Image
      maxWidth={['175px', '100%']}
      src="https://imgdrop.imgix.net/df3f4c2922ba4c4dbcc2f8142f9b3dbe.svg"
      alt="Opendoor Exclusives logo"
    />
  );
  return (
    <Modal
      {...(props as ModalProps)}
      onRequestClose={() => {
        if (!selfTourNow) {
          props.onRequestClose?.();
        } else {
          setSelfTourNow(false);
        }
      }}
      showClose
      desktopAndTabletSize="fullScreen"
      headerProps={
        {
          leftComponent: title as any,
        } as any
      }
    >
      {!selfTourNow && (
        <Flex flexDirection="column" justifyContent="space-between" flex={1} height="100%">
          <Flex flexDirection="column" gridGap="1.5rem">
            <Flex flexDirection="column">
              <Text fontSize="s2" lineHeight="s2">
                When would you like to self tour?
              </Text>
              <Text fontSize="s0" lineHeight="s0" color="neutrals90">
                Tour at your convenience, no need to bring an agent
              </Text>
            </Flex>
            <Flex
              flexDirection="row"
              overflowX="auto"
              overflowY="visible"
              gridGap="0.5rem"
              paddingY="1rem"
            >
              {days.map((date) => {
                return (
                  <SelectableCard
                    key={date.toString()}
                    isSelected={date === selectedDay}
                    onClick={() =>
                      setSelectedDay((selectedDay) => (selectedDay === date ? null : date))
                    }
                  >
                    <Flex flexDirection="column" alignItems="center">
                      <Text fontWeight="semibold">
                        {date.isSame(today) ? 'Today' : date.format('dddd')}
                      </Text>
                      <Text color="neutrals70" whiteSpace="nowrap">
                        {date.format('MMM DD')}
                      </Text>
                    </Flex>
                  </SelectableCard>
                );
              })}
            </Flex>
            {selectedDay && (
              <Flex flexDirection="column">
                <Text>Start Time</Text>
                <Flex
                  flexDirection="row"
                  overflowX="auto"
                  overflowY="visible"
                  gridGap="0.5rem"
                  paddingY="1rem"
                >
                  {isToday && !!timeRange.length && (
                    <SelectableCard
                      key="timeNow"
                      isSelected={selectedTime === todayTime}
                      onClick={() => setSelectedTime(todayTime)}
                    >
                      <Flex flexDirection="row" alignItems="center" gridGap="0.25rem">
                        <BoltIcon />
                        <Text fontWeight="semibold">Now</Text>
                      </Flex>
                    </SelectableCard>
                  )}
                  {timeRange.map((time) => {
                    return (
                      <SelectableCard
                        key={time.toString()}
                        isSelected={selectedTime === time}
                        onClick={() => setSelectedTime(time)}
                      >
                        <Text fontWeight="semibold">{timeFormat(time)}</Text>
                      </SelectableCard>
                    );
                  })}
                </Flex>
              </Flex>
            )}
            {selectedDay && selectedTime && !isNowSelected && (
              <Flex flexDirection="column">
                <Text>We’ll send a text with details an hour before.</Text>
                <Input
                  analyticsName="cosmos-exclusives-home-self-tour-modal"
                  borderRadius="rounded"
                  aria-label="Self Tour Phone Number Input"
                  id="selfTourPhoneNumberInput"
                  type="tel"
                  placeholder="Phone Number"
                  value={phoneNumber || ''}
                  onChange={(_event, value, isValid) => {
                    if (isValid) {
                      setPhoneNumber(value);
                    }
                  }}
                />
                <Text color="neutrals70" fontSize="s00">
                  By tapping "Schedule Reminder" I consent to receive marketing calls and texts from
                  Opendoor Exclusives and its affiliates to the number provided, including by
                  automated means. I may opt out at any time and consent is not a condition of
                  purchase.
                </Text>
              </Flex>
            )}
          </Flex>
          <Button
            borderRadius="rounded"
            // @ts-expect-error TODO @exclusives to resolve
            backgroundColor={exclusives.colors.brand50}
            onClick={() => {
              if (isNowSelected) {
                setSelfTourNow(true);
              } else {
                scheduleReminder();
              }
            }}
            disabled={!selectedDay || !selectedTime || (!isNowSelected && !phoneNumber)}
            loading={isScheduleLoading}
          >
            {isNowSelected ? 'Self Tour Now' : 'Schedule Reminder'}
          </Button>
        </Flex>
      )}
      {selfTourNow && <SelfTourNow />}
      <ErrorModal
        isOpen={hasError}
        modalAnalyticsName="cosmos-exclusives-self-tour-scheduling-error-modal"
        buttonAnalyticsName="cosmos-exclusives-self-tour-scheduling-error-button"
        aria-label="Home self tour scheduling error"
        title="There was an error"
        onRequestClose={() => setHasError(false)}
      >
        <Text fontSize="s1">Sorry! Please try again later.</Text>
      </ErrorModal>
    </Modal>
  );
}

function BoltIcon() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0.366534 7.58459L6.6866 0.421857C6.8668 0.217622 7.19736 0.407925 7.11123 0.66632L5.60969 5.17094C5.55572 5.33283 5.67622 5.5 5.84686 5.5H8.94601C9.16129 5.5 9.2759 5.75398 9.13347 5.9154L2.67884 13.2306C2.50559 13.427 2.18534 13.2587 2.24885 13.0046L3.42234 8.31063C3.46179 8.15285 3.34245 8 3.17981 8H0.553994C0.338714 8 0.2241 7.74602 0.366534 7.58459Z"
        fill="#EF7F81"
      />
    </svg>
  );
}

export default HomeSelfTourModal;
